// src/pages/NotasVentaPage.tsx
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../services/api'
import { useAuthStore } from '../store/authStore'
import { Plus, Search, XCircle, FileText, Printer, Share2, DollarSign, X, MoreVertical } from 'lucide-react'

interface NotaVenta {
  id_factura: number
  num_fact: string
  fecha: string
  cliente_nombre: string
  cliente_ci_ruc: string
  total: number
  estado: string
  vendedor_nombre?: string
  tiene_precio_alterado?: boolean
  total_abonado?: number
  estado_pago?: 'PENDIENTE' | 'ABONADO' | 'CANCELADO'
}

interface Abono {
  id_abono: number
  monto: number
  fecha: string
  metodo_pago: string
  observacion: string | null
}

interface HistorialAbono {
  id_historial: number
  id_abono: number
  accion: 'CREAR' | 'EDITAR'
  monto_anterior: number | null
  monto_nuevo: number
  metodo_pago_anterior: string | null
  metodo_pago_nuevo: string
  observacion_anterior: string | null
  observacion_nueva: string | null
  nombre_usuario: string
  fecha_accion: string
}

interface Distribuidor {
  id: number
  nombre: string
  email: string
}

export function NotasVentaPage() {
  const user = useAuthStore(state => state.user)
  const [notas, setNotas] = useState<NotaVenta[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [fechaInicio, setFechaInicio] = useState('')
  const [fechaFin, setFechaFin] = useState('')
  const [distribuidorFiltro, setDistribuidorFiltro] = useState('')
  const [distribuidores, setDistribuidores] = useState<Distribuidor[]>([])
  const [modalAbonosOpen, setModalAbonosOpen] = useState(false)
  const [notaSeleccionada, setNotaSeleccionada] = useState<NotaVenta | null>(null)
  const [abonos, setAbonos] = useState<Abono[]>([])
  const [totalAbonado, setTotalAbonado] = useState(0)
  const [nuevoAbono, setNuevoAbono] = useState({ monto: 0, metodo_pago: 'EFECTIVO', observacion: '' })
  const [guardandoAbono, setGuardandoAbono] = useState(false)
  const [menuAbierto, setMenuAbierto] = useState<number | null>(null)
  const [editandoAbono, setEditandoAbono] = useState<Abono | null>(null)
  const [historialAbonos, setHistorialAbonos] = useState<HistorialAbono[]>([])
  const [mostrarHistorial, setMostrarHistorial] = useState(false)

  const fetchNotas = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (fechaInicio) params.append('fecha_inicio', fechaInicio)
      if (fechaFin) params.append('fecha_fin', fechaFin)
      if (user?.rol === 'ADMINISTRADOR' && distribuidorFiltro) {
        params.append('distribuidor', distribuidorFiltro)
      }
      
      const { data } = await api.get(`/notas-venta?${params}`)
      setNotas(data.data || [])
    } catch (error) {
      console.error('Error cargando notas:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchDistribuidores = async () => {
    try {
      const { data } = await api.get('/usuarios/distribuidores')
      setDistribuidores(data.data || [])
    } catch (error) {
      console.error('Error cargando distribuidores:', error)
    }
  }

  useEffect(() => {
    if (user?.rol === 'ADMINISTRADOR') {
      fetchDistribuidores()
    }
  }, [user])

  useEffect(() => {
    fetchNotas()
  }, [fechaInicio, fechaFin, distribuidorFiltro])

  const handleAnular = async (id: number) => {
    if (!confirm('¿Está seguro de anular esta nota de venta?')) return

    try {
      await api.put(`/notas-venta/${id}/anular`)
      fetchNotas()
    } catch (error) {
      console.error('Error anulando nota:', error)
      alert('Error al anular la nota de venta')
    }
  }

  const handleCompartirPDF = (nota: NotaVenta) => {
    const baseUrl = import.meta.env.VITE_API_URL || 'https://biodacar-backend.danilogarcesmoreta2000.workers.dev'
    const pdfUrl = `${baseUrl}/api/notas-venta/${nota.id_factura}/pdf`
    
    // Mensaje para compartir
    const mensaje = `Nota de Venta ${nota.num_fact}\nCliente: ${nota.cliente_nombre}\nTotal: $${nota.total.toFixed(2)}\nVer PDF: ${pdfUrl}`
    
    // Compartir en WhatsApp
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(mensaje)}`
    window.open(whatsappUrl, '_blank')
  }

  const handleCompartirGeneral = async (nota: NotaVenta) => {
    const baseUrl = import.meta.env.VITE_API_URL || 'https://biodacar-backend.danilogarcesmoreta2000.workers.dev'
    const pdfUrl = `${baseUrl}/api/notas-venta/${nota.id_factura}/pdf`
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Nota de Venta ${nota.num_fact}`,
          text: `Cliente: ${nota.cliente_nombre}\nTotal: $${nota.total.toFixed(2)}`,
          url: pdfUrl
        })
      } catch (error) {
        console.log('Error compartiendo:', error)
      }
    } else {
      // Fallback: copiar al portapapeles
      navigator.clipboard.writeText(pdfUrl)
      alert('Link copiado al portapapeles')
    }
  }

  const handleAbrirAbonos = async (nota: NotaVenta) => {
    setNotaSeleccionada(nota)
    setModalAbonosOpen(true)
    setEditandoAbono(null)
    setMostrarHistorial(false)
    
    try {
      const { data } = await api.get(`/abonos/factura/${nota.id_factura}`)
      setAbonos(data.data || [])
      setTotalAbonado(data.total_abonado || 0)
      
      // Cargar historial
      const historialRes = await api.get(`/abonos/factura/${nota.id_factura}/historial`)
      setHistorialAbonos(historialRes.data.data || [])
    } catch (error) {
      console.error('Error cargando abonos:', error)
    }
  }

  const handleRegistrarAbono = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!notaSeleccionada || nuevoAbono.monto <= 0) return

    setGuardandoAbono(true)
    try {
      await api.post('/abonos', {
        id_factura: notaSeleccionada.id_factura,
        ...nuevoAbono
      })
      
      // Recargar abonos e historial
      const { data } = await api.get(`/abonos/factura/${notaSeleccionada.id_factura}`)
      setAbonos(data.data || [])
      setTotalAbonado(data.total_abonado || 0)
      
      const historialRes = await api.get(`/abonos/factura/${notaSeleccionada.id_factura}/historial`)
      setHistorialAbonos(historialRes.data.data || [])
      
      // Limpiar formulario
      setNuevoAbono({ monto: 0, metodo_pago: 'EFECTIVO', observacion: '' })
      
      // Recargar notas para actualizar estado de pago
      fetchNotas()
    } catch (error: any) {
      console.error('Error registrando abono:', error)
      alert(error?.response?.data?.error || 'Error al registrar abono')
    } finally {
      setGuardandoAbono(false)
    }
  }

  const handleEditarAbono = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editandoAbono || !notaSeleccionada) return

    setGuardandoAbono(true)
    try {
      await api.put(`/abonos/${editandoAbono.id_abono}`, {
        monto: editandoAbono.monto,
        metodo_pago: editandoAbono.metodo_pago,
        observacion: editandoAbono.observacion
      })
      
      // Recargar abonos e historial
      const { data } = await api.get(`/abonos/factura/${notaSeleccionada.id_factura}`)
      setAbonos(data.data || [])
      setTotalAbonado(data.total_abonado || 0)
      
      const historialRes = await api.get(`/abonos/factura/${notaSeleccionada.id_factura}/historial`)
      setHistorialAbonos(historialRes.data.data || [])
      
      setEditandoAbono(null)
      fetchNotas()
      alert('Abono actualizado exitosamente')
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Error al editar abono')
    } finally {
      setGuardandoAbono(false)
    }
  }

  const notasFiltradas = notas.filter(nota => 
    nota.num_fact.toLowerCase().includes(search.toLowerCase()) ||
    nota.cliente_nombre?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Notas de Venta</h1>
        <Link
          to="/notas-venta/nueva"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nueva Nota
        </Link>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por número o cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Fecha inicio"
            />
          </div>
          <div>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Fecha fin"
            />
          </div>
          {user?.rol === 'ADMINISTRADOR' && (
            <div>
              <select
                value={distribuidorFiltro}
                onChange={(e) => setDistribuidorFiltro(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todos los distribuidores</option>
                {distribuidores.map((dist) => (
                  <option key={dist.id} value={dist.id}>
                    {dist.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={() => { setFechaInicio(''); setFechaFin(''); setSearch(''); setDistribuidorFiltro(''); }}
            className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-50"
          >
            Limpiar filtros
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : notasFiltradas.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <FileText className="w-12 h-12 mb-4" />
            <p>No hay notas de venta</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Acciones
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Número
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendedor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Observaciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {notasFiltradas.map((nota) => (
                  <tr key={nota.id_factura} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-center relative">
                      <button
                        onClick={() => setMenuAbierto(menuAbierto === nota.id_factura ? null : nota.id_factura)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Abrir menú"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      
                      {menuAbierto === nota.id_factura && (
                        <div className="absolute left-0 top-12 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[200px]">
                          <button
                            onClick={() => {
                              const baseUrl = import.meta.env.VITE_API_URL || 'https://biodacar-backend.danilogarcesmoreta2000.workers.dev'
                              window.open(`${baseUrl}/api/notas-venta/${nota.id_factura}/pdf`, '_blank')
                              setMenuAbierto(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                          >
                            <Printer className="w-4 h-4" />
                            Ver PDF
                          </button>
                          <button
                            onClick={() => {
                              handleCompartirPDF(nota)
                              setMenuAbierto(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                            Compartir WhatsApp
                          </button>
                          <button
                            onClick={() => {
                              handleCompartirGeneral(nota)
                              setMenuAbierto(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                          >
                            <Share2 className="w-4 h-4" />
                            Compartir / Copiar
                          </button>
                          {nota.estado === 'ACTIVA' && (
                            <>
                              <div className="border-t border-gray-100 my-1"></div>
                              <button
                                onClick={() => {
                                  handleAbrirAbonos(nota)
                                  setMenuAbierto(null)
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                              >
                                <DollarSign className="w-4 h-4" />
                                Gestionar Abonos
                              </button>
                              <button
                                onClick={() => {
                                  handleAnular(nota.id_factura)
                                  setMenuAbierto(null)
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                              >
                                <XCircle className="w-4 h-4" />
                                Anular Nota
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      {nota.num_fact}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      {new Date(nota.fecha).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium">{nota.cliente_nombre || 'Consumidor Final'}</p>
                        <p className="text-sm text-gray-500">{nota.cliente_ci_ruc}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {nota.vendedor_nombre || 'Sistema'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      ${nota.total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {nota.estado === 'ANULADO' ? (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          ANULADO
                        </span>
                      ) : (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          nota.estado_pago === 'CANCELADO'
                            ? 'bg-green-100 text-green-700'
                            : nota.estado_pago === 'ABONADO'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {nota.estado_pago === 'CANCELADO' ? '✓ CANCELADO' : nota.estado_pago === 'ABONADO' ? '💰 ABONADO' : '⏳ PENDIENTE'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {nota.tiene_precio_alterado ? (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium flex items-center gap-1">
                          <span>⚠️</span>
                          <span>VALOR ALTERADO</span>
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Abonos */}
      {modalAbonosOpen && notaSeleccionada && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setModalAbonosOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">Gestión de Abonos</h2>
                <p className="text-sm text-gray-500">Nota: {notaSeleccionada.num_fact}</p>
              </div>
              <button onClick={() => setModalAbonosOpen(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Resumen */}
            <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-xs text-gray-500">Total Factura</p>
                <p className="text-lg font-bold">${notaSeleccionada.total.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Abonado</p>
                <p className="text-lg font-bold text-green-600">${totalAbonado.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Saldo Pendiente</p>
                <p className="text-lg font-bold text-orange-600">
                  ${(notaSeleccionada.total - totalAbonado).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Formulario Nuevo/Editar Abono */}
            {editandoAbono ? (
              <form onSubmit={handleEditarAbono} className="mb-6 p-4 border-2 border-blue-200 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-blue-900">Editar Abono</h3>
                  <button
                    type="button"
                    onClick={() => setEditandoAbono(null)}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancelar
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monto *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editandoAbono.monto || ''}
                      onChange={(e) => setEditandoAbono({ ...editandoAbono, monto: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Método de Pago</label>
                    <select
                      value={editandoAbono.metodo_pago}
                      onChange={(e) => setEditandoAbono({ ...editandoAbono, metodo_pago: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="EFECTIVO">Efectivo</option>
                      <option value="TRANSFERENCIA">Transferencia</option>
                      <option value="TARJETA">Tarjeta</option>
                      <option value="CHEQUE">Cheque</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      type="submit"
                      disabled={guardandoAbono || editandoAbono.monto <= 0}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {guardandoAbono ? 'Guardando...' : 'Actualizar'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observación</label>
                  <input
                    type="text"
                    value={editandoAbono.observacion || ''}
                    onChange={(e) => setEditandoAbono({ ...editandoAbono, observacion: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Opcional"
                  />
                </div>
              </form>
            ) : (
              <form onSubmit={handleRegistrarAbono} className="mb-6 p-4 border rounded-lg">
                <h3 className="font-medium mb-3">Registrar Nuevo Abono</h3>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monto *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={nuevoAbono.monto || ''}
                      onChange={(e) => setNuevoAbono({ ...nuevoAbono, monto: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                      max={notaSeleccionada.total - totalAbonado}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Método de Pago</label>
                    <select
                      value={nuevoAbono.metodo_pago}
                      onChange={(e) => setNuevoAbono({ ...nuevoAbono, metodo_pago: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="EFECTIVO">Efectivo</option>
                      <option value="TRANSFERENCIA">Transferencia</option>
                      <option value="TARJETA">Tarjeta</option>
                      <option value="CHEQUE">Cheque</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      type="submit"
                      disabled={guardandoAbono || nuevoAbono.monto <= 0}
                      className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      {guardandoAbono ? 'Guardando...' : 'Registrar'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observación</label>
                  <input
                    type="text"
                    value={nuevoAbono.observacion}
                    onChange={(e) => setNuevoAbono({ ...nuevoAbono, observacion: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Opcional"
                  />
                </div>
              </form>
            )}

            {/* Lista de Abonos con botón para mostrar historial */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">Historial de Abonos</h3>
                <button
                  onClick={() => setMostrarHistorial(!mostrarHistorial)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {mostrarHistorial ? 'Ocultar' : 'Ver'} Logs de Cambios
                </button>
              </div>

              {mostrarHistorial && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-medium text-sm text-yellow-900 mb-2">📋 Registro de Cambios (Auditoría)</h4>
                  {historialAbonos.length === 0 ? (
                    <p className="text-sm text-yellow-700">No hay cambios registrados</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {historialAbonos.map((log) => (
                        <div key={log.id_historial} className="text-xs bg-white p-2 rounded border border-yellow-100">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              log.accion === 'CREAR' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {log.accion === 'CREAR' ? '➕ CREADO' : '✏️ EDITADO'}
                            </span>
                            <span className="text-gray-500">{new Date(log.fecha_accion).toLocaleString()}</span>
                          </div>
                          <div className="text-gray-700">
                            {log.accion === 'CREAR' ? (
                              <p>
                                <strong>{log.nombre_usuario}</strong> registró abono de <strong>${log.monto_nuevo.toFixed(2)}</strong> 
                                {' '}({log.metodo_pago_nuevo})
                              </p>
                            ) : (
                              <p>
                                <strong>{log.nombre_usuario}</strong> modificó: 
                                {log.monto_anterior !== log.monto_nuevo && (
                                  <span> Monto: ${log.monto_anterior?.toFixed(2)} → ${log.monto_nuevo.toFixed(2)}</span>
                                )}
                                {log.metodo_pago_anterior !== log.metodo_pago_nuevo && (
                                  <span> | Método: {log.metodo_pago_anterior} → {log.metodo_pago_nuevo}</span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {abonos.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No hay abonos registrados</p>
              ) : (
                <div className="space-y-2">
                  {abonos.map((abono) => (
                    <div key={abono.id_abono} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className="font-medium">${abono.monto.toFixed(2)}</span>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                            {abono.metodo_pago}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(abono.fecha).toLocaleString()}
                          </span>
                        </div>
                        {abono.observacion && (
                          <p className="text-sm text-gray-600 mt-1">{abono.observacion}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setEditandoAbono(abono)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Editar abono"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

